// @ts-nocheck
/**
 * バトルAIモジュール
 * 味方オートモード / 敵AI の意思決定
 * @module twinKey/core/battle/BattleAI
 */

import { getAvailableSkills } from './SkillExecutor';
import { calculateDamagePreview, getAttributeMultiplier } from './DamageCalculator';

// ============================================================
// BattleAI クラス
// ============================================================

export class BattleAI {
  /**
   * @param {Object} state - バトル状態への参照
   */
  constructor(state) {
    this.state = state;
  }

  /**
   * 行動を決定
   * @param {Object} unit - 行動するユニット
   * @returns {{ type: string, skillId?: string, targetId: string }}
   */
  decideAction(unit) {
    if (unit.isAlly) {
      return this.decideAllyAction(unit);
    } else {
      return this.decideEnemyAction(unit);
    }
  }

  /**
   * 味方オートAI
   * 「賢いが、最適ではない」
   * - 必殺技を優先
   * - HP低い敵を狙う
   * - 属性相性を若干考慮
   * @param {Object} unit - 行動するユニット
   * @returns {{ type: string, skillId?: string, targetId: string }}
   */
  decideAllyAction(unit) {
    const enemies = this.state.enemies.filter(e => e.isAlive);
    if (enemies.length === 0) {
      return { type: 'skip' };
    }

    // 使用可能なスキルを確認
    const availableSkills = getAvailableSkills(unit);

    // 必殺技（スキル3）が使えるなら使う
    const skill3 = availableSkills.find(s => s.id === 'skill_3');
    if (skill3) {
      return {
        type: 'skill',
        skillId: 'skill_3',
        targetId: this.selectTarget(unit, enemies, 'smart'),
      };
    }

    // スキル2が使えるなら使う
    const skill2 = availableSkills.find(s => s.id === 'skill_2');
    if (skill2) {
      return {
        type: 'skill',
        skillId: 'skill_2',
        targetId: this.selectTarget(unit, enemies, 'smart'),
      };
    }

    // スキル1が使えるなら使う
    const skill1 = availableSkills.find(s => s.id === 'skill_1');
    if (skill1) {
      return {
        type: 'skill',
        skillId: 'skill_1',
        targetId: this.selectTarget(unit, enemies, 'smart'),
      };
    }

    // 通常攻撃
    return {
      type: 'attack',
      targetId: this.selectTarget(unit, enemies, 'smart'),
    };
  }

  /**
   * 敵AI
   * - スキル使用確率に基づく
   * - ランダム性を持たせる
   * @param {Object} unit - 行動するユニット
   * @returns {{ type: string, skillId?: string, targetId: string }}
   */
  decideEnemyAction(unit) {
    const allies = this.state.allies.filter(a => a.isAlive);
    if (allies.length === 0) {
      return { type: 'skip' };
    }

    const availableSkills = getAvailableSkills(unit);

    // スキル使用確率（仕様書準拠）
    // スキル3: 30%、スキル2: 40%、スキル1: 50%

    const skill3 = availableSkills.find(s => s.id === 'skill_3');
    if (skill3 && Math.random() < 0.30) {
      return {
        type: 'skill',
        skillId: 'skill_3',
        targetId: this.selectTarget(unit, allies, 'lowest_hp'),
      };
    }

    const skill2 = availableSkills.find(s => s.id === 'skill_2');
    if (skill2 && Math.random() < 0.40) {
      return {
        type: 'skill',
        skillId: 'skill_2',
        targetId: this.selectTarget(unit, allies, 'lowest_hp'),
      };
    }

    const skill1 = availableSkills.find(s => s.id === 'skill_1');
    if (skill1 && Math.random() < 0.50) {
      return {
        type: 'skill',
        skillId: 'skill_1',
        targetId: this.selectTarget(unit, allies, 'lowest_hp'),
      };
    }

    // 通常攻撃
    return {
      type: 'attack',
      targetId: this.selectTarget(unit, allies, 'lowest_hp'),
    };
  }

  /**
   * ターゲット選択
   * @param {Object} attacker - 攻撃者
   * @param {Array} targets - ターゲット候補
   * @param {string} strategy - 選択戦略
   * @returns {string} ターゲットID
   */
  selectTarget(attacker, targets, strategy) {
    if (targets.length === 0) return null;
    if (targets.length === 1) return targets[0].id;

    switch (strategy) {
      case 'lowest_hp':
        // HP割合が最も低い敵
        return targets.reduce((a, b) => {
          const aPercent = a.stats.currentHp / a.stats.maxHp;
          const bPercent = b.stats.currentHp / b.stats.maxHp;
          return aPercent < bPercent ? a : b;
        }).id;

      case 'highest_hp':
        // HP割合が最も高い敵
        return targets.reduce((a, b) => {
          const aPercent = a.stats.currentHp / a.stats.maxHp;
          const bPercent = b.stats.currentHp / b.stats.maxHp;
          return aPercent > bPercent ? a : b;
        }).id;

      case 'smart':
        return this.smartTargetSelection(attacker, targets);

      case 'weakest':
        // 防御が最も低い敵
        return targets.reduce((a, b) =>
          a.stats.defense < b.stats.defense ? a : b
        ).id;

      case 'random':
      default:
        return targets[Math.floor(Math.random() * targets.length)].id;
    }
  }

  /**
   * スマートターゲット選択
   * - 属性相性を考慮
   * - 倒せそうな敵を優先
   * @param {Object} attacker - 攻撃者
   * @param {Array} targets - ターゲット候補
   * @returns {string} ターゲットID
   */
  smartTargetSelection(attacker, targets) {
    // 各ターゲットのスコアを計算
    const scored = targets.map(target => {
      let score = 0;

      // 属性相性ボーナス
      const { effectiveness } = getAttributeMultiplier(attacker.attribute, target.attribute);
      if (effectiveness === 'effective') {
        score += 30;
      } else if (effectiveness === 'not_effective') {
        score -= 20;
      }

      // HP割合が低いほど優先（トドメを刺せる可能性）
      const hpPercent = target.stats.currentHp / target.stats.maxHp;
      score += (1 - hpPercent) * 50;

      // 予想ダメージで倒せるなら大ボーナス
      const preview = calculateDamagePreview(attacker, target, 1.0);
      if (preview.average >= target.stats.currentHp) {
        score += 100;
      }

      return { target, score };
    });

    // スコア最高のターゲットを選択
    scored.sort((a, b) => b.score - a.score);
    return scored[0].target.id;
  }
}

// ============================================================
// ユーティリティ関数
// ============================================================

/**
 * AIの判断理由をテキストで取得（デバッグ用）
 * @param {Object} unit - ユニット
 * @param {Object} action - 決定された行動
 * @param {Array} targets - ターゲット候補
 * @returns {string} 理由テキスト
 */
export function getAIDecisionReason(unit, action, targets) {
  const reasons = [];

  if (action.type === 'skill') {
    reasons.push(`スキル「${action.skillId}」が使用可能`);
  }

  if (action.targetId) {
    const target = targets.find(t => t.id === action.targetId);
    if (target) {
      const hpPercent = Math.floor((target.stats.currentHp / target.stats.maxHp) * 100);
      reasons.push(`${target.name}（HP${hpPercent}%）をターゲット`);
    }
  }

  return reasons.join(' / ') || '通常行動';
}

/**
 * ランダム性を持たせた確率判定
 * @param {number} probability - 確率（0-1）
 * @returns {boolean}
 */
export function checkProbability(probability) {
  return Math.random() < probability;
}
